import * as z from 'zod';

/** Lifecycle reported by `GET /provider-import/jobs/{job_id}`. */
export const providerImportJobStatusSchema = z.enum([
  'IMPORT_JOB_STATUS_QUEUED',
  'IMPORT_JOB_STATUS_RUNNING',
  'IMPORT_JOB_STATUS_DONE',
  'IMPORT_JOB_STATUS_FAILED',
  'IMPORT_JOB_STATUS_STALE',
]);
export type ProviderImportJobStatus = z.infer<typeof providerImportJobStatusSchema>;

export const providerImportJobKindSchema = z.enum([
  'IMPORT_JOB_KIND_RESTAURANTS',
  'IMPORT_JOB_KIND_MENU_BATCH',
  'IMPORT_JOB_KIND_CATEGORIES',
  'IMPORT_JOB_KIND_PROMOTIONS',
]);
export type ProviderImportJobKind = z.infer<typeof providerImportJobKindSchema>;

/** Created/existing/failed counters shared by every imported entity block. */
export const providerImportProgressSchema = z.object({
  created: z.number().int().nonnegative().default(0),
  already_existed: z.number().int().nonnegative().default(0),
  failed: z.number().int().nonnegative().default(0),
});
export type ProviderImportProgress = z.infer<typeof providerImportProgressSchema>;

export const providerImportFailureSchema = z.object({
  restaurant_id: z.string().default(''),
  restaurant_name: z.string().default(''),
  external_id: z.string().default(''),
  reason: z.string().default(''),
});
export type ProviderImportFailure = z.infer<typeof providerImportFailureSchema>;

/** The restaurants import trigger response. */
export const restaurantImportJobSchema = z.object({
  job_id: z.string().min(1),
  status: providerImportJobStatusSchema,
  already_running: z.boolean(),
});
export type RestaurantImportJob = z.infer<typeof restaurantImportJobSchema>;

/** The merchant-wide menu batch trigger response. */
export const menuImportBatchJobSchema = z.object({
  batch_job_id: z.string().min(1),
  already_running: z.boolean(),
});
export type MenuImportBatchJob = z.infer<typeof menuImportBatchJobSchema>;

/** A polled import job. Nullable progress blocks follow the backend contract. */
export const providerImportJobSchema = z.object({
  job_id: z.string().min(1),
  kind: providerImportJobKindSchema,
  status: providerImportJobStatusSchema,
  total_items: z.number().int().nonnegative().default(0),
  finished_items: z.number().int().nonnegative().optional(),
  failed_items: z.number().int().nonnegative().optional(),
  restaurants: providerImportProgressSchema.nullable().optional(),
  categories: providerImportProgressSchema.nullable().optional(),
  promotions: providerImportProgressSchema.nullable().optional(),
  products: providerImportProgressSchema.nullable().optional(),
  modifier_groups: providerImportProgressSchema.nullable().optional(),
  modifier_options: providerImportProgressSchema.nullable().optional(),
  failures: z.array(providerImportFailureSchema).default([]),
  failed_count: z.number().int().nonnegative().default(0),
  attempt: z.number().int().nonnegative().default(0),
  restaurant_id: z.string().default(''),
  error: z.string().nullish(),
  created_at: z.string().nullish(),
  started_at: z.string().nullish(),
  finished_at: z.string().nullish(),
  expires_at: z.string().nullish(),
});
export type ProviderImportJob = z.infer<typeof providerImportJobSchema>;

export const providerImportJobResponseSchema = z.object({
  job: providerImportJobSchema,
});
